import gql from 'graphql-tag';

import {useProjectRunStoreAcceleratorReadEnabledQuery} from '../../generated/graphql';

/**
 * The NGW gate, as one named selection.
 *
 * Apollo normalizes a Project onto `Project:<id>`, so any query that spreads
 * this keeps the gate current for every reader of it, at no extra request. That
 * is what the panel-gen history-keys query relies on: it polls, so it is what
 * notices the read ramp changing under an open workspace.
 *
 * `id` is part of the selection because only an object carrying it reaches the
 * shared entity. Spreading this without `id` in scope would write the value
 * under the query root instead, and readers would keep serving whatever they
 * fetched at page load.
 */
export const PROJECT_RSA_READ_GATE_FRAGMENT = gql`
  fragment ProjectRsaReadGate on Project {
    id
    runStoreAcceleratorReadEnabled
  }
`;

export const PROJECT_RSA_READ_ENABLED_QUERY = gql`
  query ProjectRunStoreAcceleratorReadEnabled(
    $entityName: String!
    $projectName: String!
  ) {
    project(name: $projectName, entityName: $entityName) {
      ...ProjectRsaReadGate
    }
  }
  ${PROJECT_RSA_READ_GATE_FRAGMENT}
`;

/**
 * Whether the current project's run reads are served from the Run Store
 * Accelerator (ClickHouse) for the current viewer.
 *
 * This is the read router's own decision: the accelerator is wired, the
 * project's backfill has finished, and something targets the project -- the
 * user, its entity, its org, or its own entry in the NGW limits config
 * (metadatarouting.ReadsFromAcceleratorAsPrimary). Features that assume
 * accelerator-backed reads, next-gen workspaces above all, gate on this single
 * value rather than reassembling the decision client-side.
 *
 * Returns false while the query is loading, on error, or off a project route.
 */
export const useProjectRsaReadEnabled = (
  entityName: string | undefined,
  projectName: string | undefined
): boolean => useProjectRsaReadEnabledWithLoading(entityName, projectName).data;

/**
 * As above, reporting whether the answer has arrived yet.
 *
 * Callers that branch between the accelerator and legacy paths need this:
 * `data` is false until the query resolves, so acting on it while `loading`
 * takes the legacy branch for a render and then switches.
 */
export const useProjectRsaReadEnabledWithLoading = (
  entityName: string | undefined,
  projectName: string | undefined
): {data: boolean; loading: boolean} => {
  const skip = entityName == null || projectName == null;
  const {data, loading} = useProjectRunStoreAcceleratorReadEnabledQuery({
    variables: {
      entityName: entityName ?? '',
      projectName: projectName ?? '',
    },
    skip,
  });
  return {
    data: data?.project?.runStoreAcceleratorReadEnabled === true,
    loading: loading && !skip,
  };
};
